import { z } from 'zod';
/**
 * Payload shape for `GET /api/dashboard/start-timeouts` — the
 * Two-Minute Window (TMW 4.5): how often a driver-app start is
 * accepted by the charger and then nobody plugs in before the
 * charger's ConnectionTimeOut deauthorises it, per charger and per
 * site, flagged above the threshold.
 *
 * What is counted (read-only over Session; nothing here settles
 * anything)
 * ──────────────────────────────────────────────────────────────
 * A "start" is an app-initiated start: the driver-start route
 * creates a `pending` Session shell with `payment_mode` 'wallet' or
 * 'pay_as_you_go' BEFORE RemoteStart is dispatched, and that field
 * survives promotion. RFID taps, admin remote-starts and mirrored v1
 * rows never carry it, so they are outside the denominator.
 *
 * Each start ends in exactly one bucket:
 *   charged     — the charger sent StartTransaction and the shell was
 *                 promoted (status active / completed / stopped_*).
 *   timed_out   — RemoteStart was accepted, no StartTransaction ever
 *                 came, and the platform swept the shell
 *                 (status 'failed', failure_reason 'pending_timeout_*').
 *                 Until TMW 4.4 emits activation_timeout at the
 *                 charger's real ConnectionTimeOut, the sweep is the
 *                 record of the timeout; it lands 15–20 min after the
 *                 tap, the deauthorise happened ~120 s in.
 *   late_cancel — the driver tapped Cancel AFTER the plug-in window
 *                 had already elapsed (ended_at − started_at ≥
 *                 TIMEOUT_WINDOW_SECONDS). The charger had given up
 *                 before they did; counted WITH timed_out in the rate,
 *                 reported separately so the split is visible.
 *   cancelled   — the driver cancelled inside the window (change of
 *                 mind). In the denominator, not the numerator.
 *   rejected    — the charger refused RemoteStart, or the dispatch
 *                 timed out / was unreachable (status 'failed', no
 *                 failure_reason, shell never promoted). No plug-in
 *                 opportunity existed, so NOT in the denominator;
 *                 reported so a site whose chargers refuse starts is
 *                 not mistaken for a healthy one with no demand.
 *   pending     — still in the window right now (status pending /
 *                 sweeping). Not in the denominator.
 *   unclassified— matched none of the above; expected 0, reported so
 *                 a new writer shape shows up as a number.
 *
 * Promotion is the first test: once StartTransaction has replaced the
 * shell's synthetic transaction_id the row is 'charged' whatever its
 * final status — a PowerLoss / Other stop reason finalises a charged
 * session as 'failed'. Only never-promoted shells are judged by status
 * and failure_reason.
 *
 *   starts        = charged + timed_out + late_cancel + cancelled
 *   timeout_rate  = (timed_out + late_cancel) / starts, null when 0
 *   flagged       = starts >= threshold.min_starts
 *                   AND timeout_rate > threshold.rate
 *
 * The site row is the sum of its chargers' buckets with the same
 * rule applied to the totals — a 3-stall site rarely gives one stall
 * ten starts in 30 days but the site clears the floor easily, and the
 * site is what the host is answerable for.
 */
export const START_TIMEOUT_RANGES = ['7d', '30d', '90d'];
export const StartTimeoutBucketsSchema = z.object({
    /** Accepted starts with a plug-in opportunity: charged + timed_out + late_cancel + cancelled. */
    starts: z.number().int().nonnegative(),
    charged: z.number().int().nonnegative(),
    timed_out: z.number().int().nonnegative(),
    late_cancel: z.number().int().nonnegative(),
    cancelled: z.number().int().nonnegative(),
    /** Outside the denominator. */
    rejected: z.number().int().nonnegative(),
    pending: z.number().int().nonnegative(),
    /** App-start rows that matched no bucket — a writer shape this
     *  classifier does not know yet. Expected 0; surfaced so a new
     *  failure_reason cannot silently vanish from the count. */
    unclassified: z.number().int().nonnegative(),
    /** (timed_out + late_cancel) / starts, 0–1. Null when starts is 0. */
    timeout_rate: z.number().min(0).max(1).nullable(),
    flagged: z.boolean(),
    /**
     * The diagnosis behind the rate: of the timeouts, how many had a
     * cable arrive at ANOTHER pedestal at the same site during the window
     * — a sibling charger reported StatusNotification Preparing within
     * the plug-in window (plus a grace period) while no other driver had
     * started that sibling. On a StarCharge Artemis the started unit's
     * own Preparing fires ~1.4 s after any accepted RemoteStart with no
     * cable, so a sibling's Preparing is the only thing that proves a
     * cable at all. High sibling_cable ÷ timeouts = drivers are at one
     * pedestal and starting another = the labels.
     */
    sibling_cable: z.number().int().nonnegative(),
});
export const StartTimeoutChargerRowSchema = StartTimeoutBucketsSchema.extend({
    charger_id: z.string(),
    charger_identity: z.string(),
    display_name: z.string().nullable(),
    location_id: z.string().nullable(),
    location_name: z.string().nullable(),
    /** Most recent completed charge on this charger, any start source,
     *  all time — the "no completed session in N days" signal. Null when
     *  the charger has never completed one. */
    last_charged_at: z.string().nullable(),
    /** Most recent app start in the window (tap time), null when none. */
    last_start_at: z.string().nullable(),
    /** OCPP presence: a charger with no starts has no timeout rate, so
     *  the column would read "no starts" for the failure that runs
     *  longest — a dark unit. `online` is Charger.is_online; `offline_since`
     *  is the last heartbeat when the charger is not online (null when it
     *  never heartbeated on v2, or is online). */
    online: z.boolean(),
    offline_since: z.string().nullable(),
});
export const StartTimeoutLocationRowSchema = StartTimeoutBucketsSchema.extend({
    location_id: z.string(),
    location_name: z.string(),
    charger_count: z.number().int().nonnegative(),
    flagged_charger_count: z.number().int().nonnegative(),
    /** Chargers at the site that are not online right now. */
    offline_charger_count: z.number().int().nonnegative(),
    last_charged_at: z.string().nullable(),
    last_start_at: z.string().nullable(),
});
export const StartTimeoutsResponseSchema = z.object({
    chargers: z.array(StartTimeoutChargerRowSchema),
    locations: z.array(StartTimeoutLocationRowSchema),
    flagged_charger_count: z.number().int().nonnegative(),
    flagged_location_count: z.number().int().nonnegative(),
    range: z.enum(START_TIMEOUT_RANGES),
    window_hours: z.number().int().positive(),
    threshold: z.object({
        /** Minimum accepted starts before a rate is judged. */
        min_starts: z.number().int().positive(),
        /** Flag strictly above this rate (0–1). */
        rate: z.number().min(0).max(1),
        /** Seconds after the tap past which a driver cancel counts as a
         *  timeout — the charger's plug-in window. Fixed until TMW 4.2
         *  reads each charger's ConnectionTimeOut. */
        window_seconds: z.number().int().positive(),
        /** Seconds after the tap within which a sibling pedestal's Preparing
         *  counts as "the cable went there" (window + grace). */
        sibling_window_seconds: z.number().int().positive(),
    }),
    /** Earliest StatusNotification v2 holds for the chargers in scope —
     *  the coverage of the sibling-cable signal. Null = no status history
     *  yet (pre-flip, or a fresh tenant): sibling_cable is then 0 by
     *  absence of data, not by evidence, and the UI says so. */
    sibling_history_from: z.string().nullable(),
    is_scoped: z.boolean(),
    /** Fleet tier: every app start on the fleet's chargers, whoever paid. */
    session_basis: z.enum(['scope', 'owned_chargers']).optional(),
    generated_at: z.string(),
});
//# sourceMappingURL=dashboard-start-timeouts.js.map